import React, { useState } from "react";
import { APP_NAME } from "../lib/brand.js";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";
import { landingFor } from "../lib/roles.js";

/**
 * A `?next=` the login page will honour: a same-origin path, nothing else.
 * "//evil.com" and "https://…" are refused so a crafted link can't bounce a
 * fresh sign-in off-site. Exported for the test.
 */
export function safeNext(raw) {
  if (!raw) return null;
  let v;
  try { v = decodeURIComponent(raw); } catch { return null; }
  if (!v.startsWith("/") || v.startsWith("//") || v.includes("://")) return null;
  return v;
}

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [params]                = useSearchParams();
  // Arrived via the agency door on the entry fork. Agency accounts are set up
  // with our team rather than self-serve, so this is the honest landing for
  // that door: sign in if you have an account, get in touch if you don't.
  const fromAgency              = params.get("from") === "agency";
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState(null);
  const [busy, setBusy]         = useState(false);

  // Already logged in → the server already told us where they belong.
  if (!loading && user) {
    return <Navigate to={landingFor(user)} replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await login(email.trim(), password);
      navigate(safeNext(params.get("next")) || data.redirect, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.page}>
      <div>
        {/* Wordmark */}
        <Link to="/" style={styles.wordmark}>
          <span style={styles.dot} />
          {APP_NAME}
        </Link>

        {/* Login card */}
        <div style={styles.card}>
          <h1 style={styles.heading}>Sign in</h1>
          <p style={styles.subtitle}>Welcome back.</p>

          {fromAgency && (
            <div style={styles.agencyNote}>
              Agency accounts are set up together with our team, so there's no
              sign-up form here. If your agency already has an account, sign in
              below. If not, get in touch and we'll get you started.
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label} htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              style={styles.input}
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              required
              disabled={busy}
            />

            <label style={styles.label} htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              style={styles.input}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
            />

            {error && <p style={styles.error}>{error}</p>}

            <button type="submit" style={{ ...styles.btn, ...(busy ? styles.btnBusy : {}) }} disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p style={styles.helper}>
            Setting up for a family member?{" "}
            <Link to="/start/family" style={styles.link}>Create an account</Link>
          </p>

          <p style={styles.helper}>
            Demo: lucas@example.com / lucas · dawn@example.com / dawn · michelle@example.com / michelle · edi@example.com / edi
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page:     { minHeight: "100dvh", background: "var(--color-bg-subtle)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  wordmark: { textDecoration: "none", textAlign: "center", marginBottom: 28, fontSize: 22, fontWeight: "var(--weight-medium)", letterSpacing: "-0.5px", color: "var(--color-text-primary)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  dot:      { display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "var(--color-accent)", flexShrink: 0 },
  card:     { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "40px 36px", width: "100%", maxWidth: 380 },
  heading:  { fontSize: "var(--text-xl)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", marginBottom: 4 },
  subtitle: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", marginBottom: 24 },
  form:     { display: "flex", flexDirection: "column", gap: 0 },
  label:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginBottom: 6, display: "block" },
  input:    { width: "100%", padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: "var(--text-base)", background: "var(--color-bg-subtle)", color: "var(--color-text-primary)", fontFamily: "inherit", marginBottom: 16, outline: "none" },
  error:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", marginBottom: 12 },
  btn:      { width: "100%", padding: "11px 16px", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer", marginTop: 4 },
  btnBusy:  { opacity: 0.6, cursor: "not-allowed" },
  helper:   { textAlign: "center", marginTop: 20, fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: "1.5" },
  link:       { color: "var(--color-accent)" },
  agencyNote: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "12px 14px", marginBottom: 20, lineHeight: "var(--leading-normal)" },
};
